/**
 * State Utilities for URL Slug Mapping
 * 
 * Centralizes all state code ↔ slug ↔ name conversions for:
 * - /local/[state]/ pages (formerly /local/)
 * - /rebates/[state]/ pages
 * 
 * URL Pattern: /local/california (not /local/ca)
 * Database: Stores 2-letter codes (CA, NY, etc.)
 * This file bridges the two.
 */

/**
 * Maps 2-letter state codes to URL-friendly slugs
 * Usage: STATE_SLUGS['CA'] → 'california'
 */
export const STATE_SLUGS: Record<string, string> = {
  AL: 'alabama',
  AK: 'alaska',
  AZ: 'arizona',
  AR: 'arkansas',
  CA: 'california',
  CO: 'colorado',
  CT: 'connecticut',
  DE: 'delaware',
  DC: 'district-of-columbia',
  FL: 'florida',
  GA: 'georgia',
  HI: 'hawaii',
  ID: 'idaho',
  IL: 'illinois',
  IN: 'indiana',
  IA: 'iowa',
  KS: 'kansas',
  KY: 'kentucky',
  LA: 'louisiana',
  ME: 'maine',
  MD: 'maryland',
  MA: 'massachusetts',
  MI: 'michigan',
  MN: 'minnesota',
  MS: 'mississippi',
  MO: 'missouri',
  MT: 'montana',
  NE: 'nebraska',
  NV: 'nevada',
  NH: 'new-hampshire',
  NJ: 'new-jersey',
  NM: 'new-mexico',
  NY: 'new-york',
  NC: 'north-carolina',
  ND: 'north-dakota',
  OH: 'ohio',
  OK: 'oklahoma',
  OR: 'oregon',
  PA: 'pennsylvania',
  RI: 'rhode-island',
  SC: 'south-carolina',
  SD: 'south-dakota',
  TN: 'tennessee',
  TX: 'texas',
  UT: 'utah',
  VT: 'vermont',
  VA: 'virginia',
  WA: 'washington',
  WV: 'west-virginia',
  WI: 'wisconsin',
  WY: 'wyoming',
}

/**
 * Maps URL slugs back to 2-letter state codes
 * Usage: SLUG_TO_CODE['california'] → 'CA'
 * 
 * CRITICAL: Used in page components to convert URL params to database-compatible codes
 * Example: params.state = 'california' → stateCode = 'CA' for DB queries
 */
export const SLUG_TO_CODE: Record<string, string> = {
  'alabama': 'AL',
  'alaska': 'AK',
  'arizona': 'AZ',
  'arkansas': 'AR',
  'california': 'CA',
  'colorado': 'CO',
  'connecticut': 'CT',
  'delaware': 'DE',
  'district-of-columbia': 'DC',
  'florida': 'FL',
  'georgia': 'GA',
  'hawaii': 'HI',
  'idaho': 'ID',
  'illinois': 'IL',
  'indiana': 'IN',
  'iowa': 'IA',
  'kansas': 'KS',
  'kentucky': 'KY',
  'louisiana': 'LA',
  'maine': 'ME',
  'maryland': 'MD',
  'massachusetts': 'MA',
  'michigan': 'MI',
  'minnesota': 'MN',
  'mississippi': 'MS',
  'missouri': 'MO',
  'montana': 'MT',
  'nebraska': 'NE',
  'nevada': 'NV',
  'new-hampshire': 'NH',
  'new-jersey': 'NJ',
  'new-mexico': 'NM',
  'new-york': 'NY',
  'north-carolina': 'NC',
  'north-dakota': 'ND',
  'ohio': 'OH',
  'oklahoma': 'OK',
  'oregon': 'OR',
  'pennsylvania': 'PA',
  'rhode-island': 'RI',
  'south-carolina': 'SC',
  'south-dakota': 'SD',
  'tennessee': 'TN',
  'texas': 'TX',
  'utah': 'UT',
  'vermont': 'VT',
  'virginia': 'VA',
  'washington': 'WA',
  'west-virginia': 'WV',
  'wisconsin': 'WI',
  'wyoming': 'WY',
}

/**
 * Maps 2-letter state codes to full display names
 * Usage: STATE_NAMES['CA'] → 'California'
 */
export const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

/**
 * Helper: Get URL slug from state code
 * @param code - 2-letter state code (e.g., 'CA')
 * @returns URL slug (e.g., 'california')
 */
export function getStateSlug(code: string): string {
  return STATE_SLUGS[code.toUpperCase()] || code.toLowerCase()
}

/**
 * Helper: Get state code from URL slug
 * @param slug - URL slug (e.g., 'california')
 * @returns 2-letter state code (e.g., 'CA')
 */
export function getStateCode(slug: string): string {
  return SLUG_TO_CODE[slug.toLowerCase()] || slug.toUpperCase()
}

/**
 * Helper: Get display name from code or slug
 * @param codeOrSlug - Either 'CA' or 'california'
 * @returns Display name (e.g., 'California')
 */
export function getStateName(codeOrSlug: string): string {
  // Try as code first
  if (STATE_NAMES[codeOrSlug.toUpperCase()]) {
    return STATE_NAMES[codeOrSlug.toUpperCase()]
  }
  // Try converting slug to code
  const code = SLUG_TO_CODE[codeOrSlug.toLowerCase()]
  if (code && STATE_NAMES[code]) {
    return STATE_NAMES[code]
  }
  // Fallback: Title case the input
  return codeOrSlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Helper: Check if a string is a valid state slug
 * @param slug - String to check
 * @returns true if valid state slug
 */
export function isValidStateSlug(slug: string): boolean {
  return slug.toLowerCase() in SLUG_TO_CODE
}

/**
 * Helper: Check if a string is a valid state code
 * @param code - String to check
 * @returns true if valid state code
 */
export function isValidStateCode(code: string): boolean {
  return code.toUpperCase() in STATE_SLUGS
}

/**
 * Get all state codes as an array
 * Useful for generateStaticParams
 */
export const ALL_STATE_CODES = Object.keys(STATE_SLUGS)

/**
 * Get all state slugs as an array
 * Useful for validation
 */
export const ALL_STATE_SLUGS = Object.keys(SLUG_TO_CODE)
